import { useState } from "react";
import Modal from "../common/Modal";
import {
  createGroup,
  getGroupByName
} from "../../common/api/groups";
import {
  getUserByUsername,
  updateUser
} from "../../common/api/users";
import { apiFetch } from "../../common/api";
import { hashPassword } from "../../common/api/auth";
import { useToast } from "../../hooks/useToast";

const normalizeGroupNames = (groups, targetClientCode = null) => {
  if (!Array.isArray(groups)) return [];

  const normalizedTarget =
    targetClientCode !== null && targetClientCode !== undefined
      ? String(targetClientCode).trim()
      : null;

  return groups
    .map((group) => {
      if (typeof group === "string") {
        return { name: group, clientCode: null };
      }

      if (group && typeof group === "object") {
        const name =
          typeof group.NOMBRE === "string"
            ? group.NOMBRE
            : typeof group.nombre === "string"
              ? group.nombre
              : typeof group.name === "string"
                ? group.name
                : null;

        const clientRaw =
          group.COD_CLIENTE ??
          group.cod_cliente ??
          group.COD_ID_GROUP ??
          group.cod_id_group ??
          null;

        const clientCode =
          clientRaw !== null && clientRaw !== undefined ? String(clientRaw) : null;

        return { name, clientCode };
      }

      return null;
    })
    .filter((entry) => entry && typeof entry.name === "string" && entry.name.trim().length > 0)
    .filter((entry) => {
      if (!normalizedTarget) return true;
      if (!entry.clientCode) return true;
      return entry.clientCode === normalizedTarget;
    })
    .map((entry) => entry.name.trim());
};


function ModalNewGroup({ selectedClientCode , setGroups, setUsers, onClose, availableMembers, adminUser , setAllUsersCached,
  setAllGroupsCached}) {
  const [groupName, setGroupName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [adminPassword, setAdminPassword] = useState("");
  const toast = useToast();

  const toggleMember = (member) => {
    setSelectedMembers((prev) =>
      prev.includes(member)
        ? prev.filter((m) => m !== member)
        : [...prev, member]
    );
  };

  // Helper per ottenere utenti del cliente
  const getUsersByClient = async (selectedClientCode ) => {
    const res = await apiFetch(`/users?ID=${selectedClientCode }`);
    if (!res.ok) throw new Error("No se pudieron obtener los usuarios");
    return await res.json();
  };

  const handleCreate = async () => {
  if (!groupName.trim()) {
    toast.error("Por favor ingresa el nombre del grupo");
    return;
  }

  if (selectedMembers.length > 0 && !adminPassword.trim()) {
    toast.error("Debes ingresar tu contraseña para asignar miembros");
    return;
  }

  if (!selectedClientCode  || selectedClientCode .trim() === "") {
    toast.error("No se ha podido determinar el cliente para crear el grupo.");
    return;
  }

  const trimmedName = groupName.trim();

  try {
    // 1. Ottieni utenti del cliente corrente per verificare se il gruppo esiste
    const basicUsers = await getUsersByClient(selectedClientCode );
    const detailedUsers = await Promise.all(
      basicUsers.map((u) => getUserByUsername(u.USER))
    );

    // Estrai gruppi unici esistenti
    const existingGroups = new Set();
    detailedUsers.forEach(user => {
      const groupNames = normalizeGroupNames(user.GRUPOS,selectedClientCode);
      groupNames.forEach((groupName) => existingGroups.add(groupName.toLowerCase()));
    });

    if (existingGroups.has(trimmedName.toLowerCase())) {
      toast.error("Ya existe un grupo con ese nombre para este cliente.");
      return;
    }

    /*console.log("📤 Creando grupo:", {
      COD_CLIENTE: selectedClientCode ,
      NOMBRE: trimmedName,
      ACTIVO: enabled ? "1" : "0"
    });*/

    // 2. Crea il gruppo
    const created = await createGroup({
      COD_CLIENTE: selectedClientCode ,
      NOMBRE: trimmedName,
      ACTIVO: enabled ? "1" : "0",
    });

    //console.log("📥 Respuesta createGroup:", created);

    if (!created || created.status === "Error") {
      toast.error("No se pudo crear el grupo.");
      return;
    }

    toast.success("Grupo creado correctamente.");

    // 3. Asignar miembros se selezionati
    const successUsers = [];
    const failedUsers = [];

    if (selectedMembers.length > 0) {
      const CURR_PASS_HASH = await hashPassword(adminPassword);

      for (const username of selectedMembers) {
        try {
          //console.log(`🔄 Intentando agregar ${username} al grupo...`);
          
          const user = await getUserByUsername(username);
          if (!user) {
            //console.log(`❌ Usuario ${username} no encontrado`);
            failedUsers.push(username);
            continue;
          }

           const currentGroups = normalizeGroupNames(user.GRUPOS,selectedClientCode);
          const hasGroupAssigned = currentGroups.some(
            (groupName) => groupName.toLowerCase() === trimmedName.toLowerCase()
          );
          const updatedGroups = hasGroupAssigned
            ? currentGroups
            : [...currentGroups, trimmedName];

          const payload = {
            COD_ID: user.COD_ID,
            USER: user.USER,
            //EMAIL: user.EMAIL,
            //ACTIVO: user.ACTIVO ?? "1",
            GRUPOS: updatedGroups,
            COD_ID_GROUP: String(selectedClientCode ),
            CURR_PASS_HASH,
          };

          //console.log(`📤 Payload para ${username}:`, payload);

          await updateUser(payload);
          //console.log(`✅ Usuario ${username} agregado correctamente`);
          successUsers.push(username);
        } catch (err) {
          console.error(`❌ Error actualizando ${username}:`, err);
          console.error(`❌ Detalle del error:`, err.message);
          failedUsers.push(username);
        }
      }

      // Mostrar resumen
      if (successUsers.length > 0 && failedUsers.length > 0) {
        toast.success(`Agregados: ${successUsers.join(", ")}`);
        toast.error(`No se pudieron agregar: ${failedUsers.join(", ")}`);
      } else if (successUsers.length > 0) {
        toast.success(`Todos los miembros fueron agregados correctamente (${successUsers.length})`);
      } else if (failedUsers.length > 0) {
        toast.error(`No se pudo agregar ningún usuario: ${failedUsers.join(", ")}`);
      }
    }

    // 4. Ricarica dati del cliente corrente
    const updatedBasicUsers = await getUsersByClient(selectedClientCode );
    const updatedDetailedUsers = await Promise.all(
      updatedBasicUsers.map((u) => getUserByUsername(u.USER))
    );

    // Estrai gruppi unici aggiornati
    const uniqueGroupNames = new Set();
    updatedDetailedUsers.forEach(user => {
      const groupNames = normalizeGroupNames(user.GRUPOS,selectedClientCode);
      groupNames.forEach((groupName) => uniqueGroupNames.add(groupName));
    });

    // Ottieni dettagli completi dei gruppi
    const groupDetails = await Promise.all(
     Array.from(uniqueGroupNames).map(async (name) => {
        if (typeof name !== "string" || name.trim().length === 0) {
          return null;
        }

        try {
          return await getGroupByName(name);
        } catch (err) {
          console.warn(`No se pudo obtener el grupo ${name}:`, err);
          return null;
        }
      })
    );

    const sanitizedGroups = groupDetails
      .filter((group) => group && typeof group === "object")
      .map((group) => {
        const nombreNormalizado =
          typeof group.NOMBRE === "string"
            ? group.NOMBRE
            : typeof group.nombre === "string"
              ? group.nombre
              : typeof group.name === "string"
                ? group.name
                : "";

        const memberCount = updatedDetailedUsers.filter((user) => {
          const groupNames = normalizeGroupNames(user.GRUPOS,selectedClientCode);
          return groupNames.some(
            (groupName) => groupName.toLowerCase() === nombreNormalizado.toLowerCase()
          );
        }).length;

        return {
          ...group,
          NOMBRE: nombreNormalizado,
          memberCount,
        };
      })
      .filter((group) => group.NOMBRE);

    setGroups(sanitizedGroups);
    setUsers(updatedDetailedUsers);
    if (typeof setAllUsersCached === "function") {
      setAllUsersCached(updatedDetailedUsers);
    }

    if (typeof setAllGroupsCached === "function") {
      setAllGroupsCached(sanitizedGroups);
    }
    onClose();
  } catch (err) {
    console.error("Error inesperado:", err);
    toast.error(err.message || "Ocurrió un error inesperado al crear el grupo.");
  }
};

  return (
    <Modal title="Nuevo Grupo" onClose={onClose}>
      <div className="input-group">
        <label className="label">Nombre del grupo</label>
        <input
          className="input"
          type="text"
          value={groupName}
          onChange={(e) => setGroupName(e.target.value)}
        />
      </div>

      <div className="input-group">
        <label className="label">Seleccionar miembros para el grupo</label>
        <div className="lista" style={{ maxHeight: "200px", overflowY: "auto" }}>
          {availableMembers.map((member) => (
            <span key={member}>
              <input
                type="checkbox"
                checked={selectedMembers.includes(member)}
                onChange={() => toggleMember(member)}
              />
              {member}
              {adminUser && member === adminUser.USER && (
                <span style={{ color: "#4a9eff", marginLeft: "5px" }}>(Tú)</span>
              )}
            </span>
          ))}
        </div>
      </div>

      <div className="input-group_check">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => setEnabled(e.target.checked)}
        />
        <label>Habilitado</label>
      </div>

      {selectedMembers.length > 0 && (
        <div className="input-group">
          <label className="label">Tu contraseña (para asignar miembros)</label>
          <input
            type="password"
            className="input"
            value={adminPassword}
            onChange={(e) => setAdminPassword(e.target.value)}
          />
        </div>
      )}

      <div className="input-group_row">
        <span className="boton_opciones" onClick={handleCreate}>
          Aceptar
        </span>
        <span className="boton_cancelar" onClick={onClose}>
          Cancelar
        </span>
      </div>
    </Modal>
  );
}

export default ModalNewGroup;